import { useCallback, useContext, useMemo } from "react"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { XmppContext } from "@/stream/xmppClient"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import {
  selectChats,
  selectChatCounts,
  selectSlickshiftAIChat,
} from "@/reducers/chatsSliceSelectors"
import { selectUserChats } from "@/reducers/userChatsSlice"
import { addBookmark } from "@/api/xeps/PepNativeBookmarks"
import { joinChat } from "@/reducers/chatsSliceThunks"
import { findTranschatUserChat } from "@/lib/privateAgentUtils"

// The user's private agent chat: their transchat room, else the Slickshift AI
// chat. Without the private-agent flag there is no target.
export const usePrivateAgentTarget = (openChat: (chatJid: string) => void) => {
  const { myJid, client } = useContext(XmppContext)
  const dispatch = useAppDispatch()
  const privateAgentEnabled = Boolean(useFeatureFlagEnabled("private-agent"))

  const slickshiftChatSelector = useMemo(
    () => selectSlickshiftAIChat(myJid),
    [myJid],
  )

  const slickshiftAIChat = useAppSelector(slickshiftChatSelector)
  const chats = useAppSelector(selectChats)
  const userChats = useAppSelector(selectUserChats)

  const chatCountsSelector = useMemo(
    () => selectChatCounts(myJid.toString()),
    [myJid],
  )
  const chatCounts = useAppSelector(chatCountsSelector)

  const transchatUserChat = useMemo(
    () => findTranschatUserChat(userChats, myJid.local),
    [userChats, myJid.local],
  )

  const isTranschatUserChatJoined = useMemo(
    () =>
      Boolean(
        transchatUserChat &&
          chats.some((chat) => chat.jid === transchatUserChat.jid),
      ),
    [chats, transchatUserChat],
  )

  const targetJid = privateAgentEnabled
    ? (transchatUserChat?.jid ?? slickshiftAIChat?.jid)
    : undefined

  const unreadCount = targetJid ? (chatCounts[targetJid] ?? 0) : 0

  const openPrivateAgentChat = useCallback(
    async (source: string) => {
      if (!targetJid) return

      if (transchatUserChat && !isTranschatUserChatJoined) {
        await addBookmark(client, transchatUserChat.jid, transchatUserChat.name)
        window.analytics.track("AddBookmark", {
          chatJid: transchatUserChat.jid,
          chatName: transchatUserChat.name,
          from: source,
        })
        dispatch(joinChat(client, transchatUserChat))
      }

      openChat(targetJid)
    },
    [
      client,
      dispatch,
      isTranschatUserChatJoined,
      openChat,
      targetJid,
      transchatUserChat,
    ],
  )

  return { openPrivateAgentChat, targetJid, unreadCount }
}
